/*
 * Stripe's card fields on the quote page (owner, 2026-10-30: "the save card doesn't work"). The fields load as soon as the
 * box shows, with no extra Add a card tap: cards, Apple Pay and Google Pay only (a bank account or Klarna saved nothing).
 * Like the invoice pay page (owner, Oct 31): Apple Pay or Google Pay is its own button on top, then OR, then the card
 * fields (US, no country box). The wallet button saves the card right away; the parent's button then books.
 * The parent's one button (Book, or Approve and save card) calls save(), then books or approves, so the client signs and
 * saves the card in one tap, like the old GroundsMen quote page. A test server (no Stripe) saves the test card 4242.
 */
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js";
import { CARD_TERMS, type PublicQuote } from "@steward/shared";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "./parts";
import { api } from "../api/client";
import type { PayStart } from "../api/money";
import { errText } from "./work";

type CardState = "loading" | "ready" | "test" | "error";

export interface CardFields<T = PublicQuote> {
  box: React.RefObject<HTMLDivElement | null>;
  /** Apple Pay or Google Pay, shown only on a phone or browser that has one. */
  walletBox: React.RefObject<HTMLDivElement | null>;
  hasWallet: boolean;
  /** Saved with Apple Pay or Google Pay already: the parent's button only books. */
  walletSaved: boolean;
  state: CardState;
  err: string | null;
  /** Saves the card with Stripe, then on the quote (or the save a card page). Throws with words for the client when it
   * did not save. */
  save: () => Promise<T>;
}

/** The brand colors the page runs on, for Stripe's fields (they draw inside their own frame). */
function lookOf(el: HTMLElement | null) {
  const css = el ? getComputedStyle(el) : null;
  const v = (name: string, or: string) => {
    const got = css?.getPropertyValue(name).trim();
    return got !== undefined && got !== "" ? got : or;
  };
  return {
    theme: "stripe" as const,
    variables: {
      colorPrimary: v("--brand-deep", "#1f6f5c"),
      colorText: "#1d1d1f",
      borderRadius: "10px",
      fontSizeBase: "16px",
      spacingUnit: "4px",
    },
  };
}

/** base: where the page's card-start and card-done live, "public/quote" or "public/card" (our save a card page). */
export function useCardFields<T = PublicQuote>(token: string, on: boolean, base = "public/quote"): CardFields<T> {
  const box = useRef<HTMLDivElement | null>(null);
  const walletBox = useRef<HTMLDivElement | null>(null);
  const [hasWallet, setHasWallet] = useState(false);
  const [walletSaved, setWalletSaved] = useState(false);
  const [state, setState] = useState<CardState>("loading");
  const [err, setErr] = useState<string | null>(null);
  const made = useRef<{ setup: PayStart; stripe: { s: Stripe; e: StripeElements } | null } | null>(null);

  useEffect(() => {
    if (!on) return;
    // Held in an object so a late answer from Stripe after the box left the page is dropped.
    const life: { gone: boolean; destroy: (() => void)[] } = { gone: false, destroy: [] };
    const left = () => life.gone;
    setState("loading");
    setErr(null);
    void (async () => {
      try {
        const setup = await api<PayStart>("POST", `${base}/${token}/card-start`, {});
        if (left()) return;
        if (setup.fake || !setup.publishableKey) {
          made.current = { setup, stripe: null };
          setState("test");
          return;
        }
        const s = await loadStripe(setup.publishableKey);
        if (!s) throw new Error("The card fields could not load. Check your connection and try again.");
        if (left() || !box.current) return;
        const e = s.elements({ clientSecret: setup.clientSecret, appearance: lookOf(box.current) });
        // Just the card, like the invoice page: the wallets have their own button above, and the country is US. Stripe's
        // own consent line is left off: the box shows the brand's sentence under the fields.
        const pay = e.create("payment", {
          layout: "tabs",
          fields: { billingDetails: { address: { country: "never" } } },
          wallets: { applePay: "never", googlePay: "never", link: "never" },
          terms: { card: "never" },
        });
        pay.on("ready", () => {
          if (!left()) setState("ready");
        });
        pay.on("loaderror", () => {
          if (left()) return;
          setErr("The card fields could not load. Check your connection and try again.");
          setState("error");
        });
        pay.mount(box.current);
        life.destroy.push(() => pay.destroy());
        made.current = { setup, stripe: { s, e } };
        if (walletBox.current) {
          const we = s.elements({ mode: "setup", currency: "usd", paymentMethodTypes: ["card"] });
          const ece = we.create("expressCheckout", {
            buttonHeight: 52,
            paymentMethods: { applePay: "auto", googlePay: "auto", link: "never", paypal: "never", amazonPay: "never" },
          });
          ece.on("ready", (ev) => {
            if (!left()) setHasWallet(Boolean(ev.availablePaymentMethods));
          });
          ece.on("confirm", () => {
            void (async () => {
              try {
                const checked = await we.submit();
                if (checked.error) throw new Error(checked.error.message ?? "That did not go through.");
                const r = await s.confirmSetup({
                  elements: we,
                  clientSecret: setup.clientSecret,
                  confirmParams: { return_url: window.location.href },
                  redirect: "if_required",
                });
                if (r.error) throw new Error(r.error.message ?? "The card was not saved. Try the card fields.");
                if (!left()) setWalletSaved(true);
              } catch (x) {
                if (!left()) setErr(x instanceof Error ? x.message : errText(x, "The card was not saved."));
              }
            })();
          });
          ece.mount(walletBox.current);
          life.destroy.push(() => ece.destroy());
        }
      } catch (x) {
        if (left()) return;
        setErr(x instanceof Error && !(x instanceof TypeError) ? x.message : errText(x, "The card fields could not load."));
        setState("error");
      }
    })();
    return () => {
      life.gone = true;
      for (const d of life.destroy) d();
      setHasWallet(false);
      setWalletSaved(false);
    };
  }, [token, on, base]);

  const save = async (): Promise<T> => {
    const m = made.current;
    if (!m || state === "loading") throw new Error("The card fields are still loading. Try again in a moment.");
    if (state === "error") throw new Error(err ?? "The card fields could not load.");
    if (m.stripe && !walletSaved) {
      const r = await m.stripe.s.confirmSetup({
        elements: m.stripe.e,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: { billing_details: { address: { country: "US" } } },
        },
        redirect: "if_required",
      });
      if (r.error) throw new Error(r.error.message ?? "The card was not saved. Check it or try another card.");
    }
    return api<T>("POST", `${base}/${token}/card-done`, { paymentIntent: m.setup.intentId });
  };

  return { box, walletBox, hasWallet, walletSaved, state, err, save };
}

/** The card box: Stripe's fields, a loading line until they show, and the words the client agrees to. */
export function CardBox<T>({ card, title, sentence }: { card: CardFields<T>; title: string; sentence: ReactNode }) {
  return (
    <section className="card-box">
      <p className="card-box-title">{title}</p>
      {card.state === "loading" ? <div className="card-box-wait">Loading secure card fields</div> : null}
      {card.state === "test" ? <p className="muted small">Test mode here. Saving uses the test card 4242.</p> : null}
      {card.state === "error" ? <p className="card-box-err">{card.err}</p> : null}
      {card.walletSaved ? (
        <p className="card-box-title">Card saved with Apple Pay or Google Pay. Press the button below to finish.</p>
      ) : (
        <>
          <div ref={card.walletBox} className={`cp-wallet${card.hasWallet ? " on" : ""}`} />
          {card.hasWallet ? <div className="cp-or">OR</div> : null}
        </>
      )}
      {card.err && card.state !== "error" ? <p className="card-box-err">{card.err}</p> : null}
      <div ref={card.box} className={`card-box-fields${card.state === "ready" ? " on" : ""}`} hidden={card.walletSaved} />
      <p className="card-box-words">{sentence}</p>
      <p className="card-box-safe">
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            fill="currentColor"
            d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V11a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm-3 8V6a3 3 0 1 1 6 0v3H9Z"
          />
        </svg>
        Secured by Stripe. We never see your card number.
      </p>
    </section>
  );
}

/** Card terms (owner, 2026-11-02): the short agreement for a card on file, from the footer and next to every card save. */
export function CardTermsLink() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <>
      <button type="button" className="terms-link" onClick={() => setOpen(true)}>
        Card terms
      </button>
      {/* Drawn inside the client page (not the app popup), so it keeps the page light and its brand colors. */}
      {open ? (
        <div className="terms-scrim" onClick={() => setOpen(false)}>
          <div
            className="terms-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Card terms"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Card terms</h2>
            <ul className="card-terms">
              {CARD_TERMS.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
            <Button fullWidth onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
